import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Download, X } from 'lucide-react';
import { usePWA } from '@/hooks/usePWA';

interface PWAInstallPromptProps {
  onDismiss: () => void;
}

const INSTALL_PROMPT_DISMISSED_KEY = 'pwa-install-dismissed';
const INSTALL_PROMPT_DELAY = 30000; // 30 seconds

export const PWAInstallPrompt: React.FC<PWAInstallPromptProps> = ({ onDismiss }) => {
  const { installApp, isInstallable } = usePWA();
  const [shouldShow, setShouldShow] = useState(false);

  useEffect(() => {
    // Check if user previously dismissed
    const dismissed = localStorage.getItem(INSTALL_PROMPT_DISMISSED_KEY);
    if (dismissed) {
      return;
    }

    // Show prompt after delay if installable
    if (isInstallable) {
      const timer = setTimeout(() => {
        setShouldShow(true);
      }, INSTALL_PROMPT_DELAY);

      return () => clearTimeout(timer);
    }
  }, [isInstallable]);

  if (!isInstallable || !shouldShow) return null;

  const handleInstall = async () => {
    await installApp();
    setShouldShow(false);
    onDismiss();
  };

  const handleDismiss = () => {
    localStorage.setItem(INSTALL_PROMPT_DISMISSED_KEY, 'true');
    setShouldShow(false);
    onDismiss();
  };

  return (
    <Card className="fixed bottom-20 left-4 right-4 z-40 mx-auto max-w-sm border-primary/20 bg-card/95 backdrop-blur-sm sm:bottom-4 sm:left-auto sm:right-4 safe-area-inset-bottom">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <Download className="h-5 w-5 text-primary" />
            <CardTitle className="text-sm">Install Neo Academy</CardTitle>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 min-h-[44px] min-w-[44px] -mt-2 -mr-2"
            onClick={handleDismiss}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        <CardDescription className="text-xs">
          Get faster access and offline learning capabilities
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="flex gap-2">
          <Button
            size="sm"
            onClick={handleInstall}
            className="flex-1 h-10"
          >
            Install App
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleDismiss}
            className="flex-1 h-10"
          >
            Maybe Later
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};